import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { api } from '@/App';
import { Button } from '@/components/ui/button';
import { MapPin, TrendingUp, Building2, DollarSign, Calendar, Clock } from 'lucide-react';
import { toast } from 'sonner';
import ConstructionStageChart from '@/components/ConstructionStageChart';
import AppreciationChart from '@/components/AppreciationChart';
import { formatCurrency, formatCurrencyShort, formatCurrencyDetailed } from '@/utils/currency';

export default function PropertyDetail({ user }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [investmentAmount, setInvestmentAmount] = useState(0);
  const [investing, setInvesting] = useState(false);

  useEffect(() => {
    const fetchProperty = async () => {
      try {
        const response = await api.get(`/properties/${id}`);
        setProperty(response.data);
        setInvestmentAmount(response.data.price_per_share);
      } catch (error) {
        console.error('Error fetching property:', error);
        toast.error('Property not found');
        navigate('/properties');
      } finally {
        setLoading(false);
      }
    };
    fetchProperty();
  }, [id, navigate]);

  const handleInvestment = async () => {
    if (!user) {
      toast.error('Please sign in to invest');
      navigate('/auth');
      return;
    }

    if (investmentAmount < property.price_per_share) {
      toast.error(`Minimum investment is ${formatCurrency(property.price_per_share)}`);
      return;
    }

    const shares = investmentAmount / property.price_per_share;
    if (shares > property.available_shares) {
      toast.error('Not enough shares available');
      return;
    }

    setInvesting(true);
    try {
      await api.post('/investments', {
        property_id: property.id,
        amount: investmentAmount
      });
      toast.success('Investment successful!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Investment failed');
    } finally {
      setInvesting(false);
    }
  };

  if (loading || !property) {
    return (
      <div className="min-h-screen pt-32 flex items-center justify-center">
        <div className="text-white/60">Loading property...</div>
      </div>
    );
  }

  const maxInvestment = property.available_shares * property.price_per_share;
  const ownershipPercentage = (investmentAmount / property.total_value) * 100;
  const shares = investmentAmount / property.price_per_share;
  const sliderPercentage = ((investmentAmount - property.price_per_share) / (maxInvestment - property.price_per_share)) * 100;

  // Construction stages data for off-plan properties
  const constructionStages = [
    {
      id: 'foundation',
      name: 'Foundation',
      description: 'Site preparation and foundation work',
      timeline: 'Months 1-3',
      progress: 100,
      valueIncrease: 5
    },
    {
      id: 'structure',
      name: 'Structure',
      description: 'Main building structure and framing',
      timeline: 'Months 4-8',
      progress: property.construction_stage === 'structure' || property.construction_stage === 'finishing' || property.construction_stage === 'completed' ? 100 : property.construction_stage === 'structure' ? 60 : 0,
      valueIncrease: 10
    },
    {
      id: 'finishing',
      name: 'Finishing',
      description: 'Interior finishing and installations',
      timeline: 'Months 9-12',
      progress: property.construction_stage === 'finishing' || property.construction_stage === 'completed' ? 75 : 0,
      valueIncrease: 15
    },
    {
      id: 'completion',
      name: 'Completion',
      description: 'Final inspections and handover',
      timeline: 'Month 12-14',
      progress: property.construction_stage === 'completed' ? 100 : 0,
      valueIncrease: 25
    }
  ];

  const currentStage = constructionStages.find(s => s.id === property.construction_stage) || constructionStages[1];
  currentStage.progress = property.construction_progress || 60;

  return (
    <div className="min-h-screen pt-32 pb-24">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Off-Plan Badge */}
          {property.is_off_plan && (
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-[#4ADE80]/10 border border-[#4ADE80]/20 rounded-full mb-6">
              <Clock className="w-4 h-4 text-[#4ADE80]" />
              <span className="text-sm text-[#4ADE80] font-medium">Off-Plan Property - Under Construction</span>
            </div>
          )}

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Left Column */}
            <div>
              <div className="relative rounded-2xl overflow-hidden mb-6">
                <img 
                  src={property.image_url} 
                  alt={property.title}
                  className="w-full h-[400px] object-cover"
                  data-testid="property-image"
                />
                <div className="absolute top-6 right-6 px-4 py-2 bg-[#4ADE80] text-black text-sm font-medium rounded-full flex items-center space-x-2">
                  <TrendingUp className="w-4 h-4" />
                  <span>{property.annual_return}% APY</span>
                </div>
                {property.is_off_plan && (
                  <div className="absolute bottom-6 left-6 right-6">
                    <div className="glass rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-white/60">Construction Progress</span>
                        <span className="text-sm text-[#4ADE80] font-mono">{property.construction_progress}%</span>
                      </div>
                      <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-[#4ADE80] transition-all duration-500"
                          style={{ width: `${property.construction_progress}%` }}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="bg-[#0A0A0A] border border-white/5 rounded-xl p-8">
                <h1 className="text-3xl font-light mb-4" data-testid="property-title">{property.title}</h1>
                <div className="flex items-center space-x-2 text-white/60 mb-6">
                  <MapPin className="w-4 h-4" />
                  <span data-testid="property-location">{property.location}</span>
                </div>
                <p className="text-white/80 leading-relaxed mb-8" data-testid="property-description">
                  {property.description}
                </p>

                <div className="grid grid-cols-2 gap-6">
                  <div className="bg-[#111111] rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <Building2 className="w-4 h-4 text-[#4ADE80]" />
                      <span className="text-xs text-white/40">Property Type</span>
                    </div>
                    <p className="text-lg" data-testid="property-type">{property.property_type}</p>
                  </div>
                  <div className="bg-[#111111] rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <DollarSign className="w-4 h-4 text-[#4ADE80]" />
                      <span className="text-xs text-white/40">{property.is_off_plan ? 'Current Value' : 'Total Value'}</span>
                    </div>
                    <p className="text-lg font-mono" data-testid="property-total-value">
                      {formatCurrencyShort(property.is_off_plan ? property.current_value : property.total_value)}
                    </p>
                  </div>
                  {property.is_off_plan && (
                    <>
                      <div className="bg-[#111111] rounded-lg p-4">
                        <div className="flex items-center space-x-2 mb-2">
                          <Calendar className="w-4 h-4 text-[#4ADE80]" />
                          <span className="text-xs text-white/40">Expected Completion</span>
                        </div>
                        <p className="text-lg">{property.completion_date || 'Q4 2025'}</p>
                      </div>
                      <div className="bg-[#111111] rounded-lg p-4">
                        <div className="flex items-center space-x-2 mb-2">
                          <TrendingUp className="w-4 h-4 text-[#4ADE80]" />
                          <span className="text-xs text-white/40">Projected Value</span>
                        </div>
                        <p className="text-lg font-mono text-[#4ADE80]">
                          {formatCurrencyShort(property.projected_value)}
                        </p>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Construction Stage Chart for Off-Plan */}
              {property.is_off_plan && (
                <div className="mt-6">
                  <ConstructionStageChart 
                    currentStage={currentStage}
                    stages={constructionStages}
                  />
                </div>
              )}

              {/* Appreciation Chart for Off-Plan */}
              {property.is_off_plan && property.projected_value && (
                <div className="mt-6">
                  <AppreciationChart
                    initialValue={property.total_value}
                    currentValue={property.current_value || property.total_value}
                    projectedValue={property.projected_value}
                  />
                </div>
              )}
            </div>

            {/* Right Column - Investment Calculator */}
            <div className="lg:sticky lg:top-32 h-fit">
              <div className="glass rounded-2xl p-8 border border-white/10">
                <h2 className="text-2xl font-light mb-8">Investment Calculator</h2>
                
                {property.is_off_plan && (
                  <div className="mb-8 p-4 bg-[#4ADE80]/5 border border-[#4ADE80]/20 rounded-lg">
                    <p className="text-sm text-white/80">
                      <strong className="text-[#4ADE80]">Off-Plan Advantage:</strong> Invest now at current construction stage pricing. 
                      Your investment will appreciate as the property nears completion.
                    </p>
                  </div>
                )}

                <div className="space-y-8">
                  <div>
                    <label className="text-sm text-white/60 mb-4 block">Investment Amount</label>
                    <div className="investment-slider">
                      <input 
                        type="range"
                        min={property.price_per_share}
                        max={maxInvestment}
                        step={property.price_per_share}
                        value={investmentAmount}
                        onChange={(e) => setInvestmentAmount(Number(e.target.value))}
                        style={{ '--value': `${sliderPercentage}%` }}
                        data-testid="investment-slider"
                      />
                    </div>
                    <div className="flex items-center justify-between mt-4">
                      <span className="text-xs text-white/40">{formatCurrency(property.price_per_share)}</span>
                      <span className="text-xs text-white/40">{formatCurrencyShort(maxInvestment)}</span>
                    </div>
                  </div>

                  <div className="bg-[#111111] rounded-xl p-6 space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-white/60">Investment</span>
                      <span className="text-2xl font-mono" data-testid="investment-amount">{formatCurrencyDetailed(investmentAmount)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-white/60">Shares</span>
                      <span className="text-xl font-mono" data-testid="investment-shares">{shares.toFixed(2)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-white/60">Ownership</span>
                      <span className="text-xl font-mono text-[#4ADE80]" data-testid="investment-ownership">{ownershipPercentage.toFixed(4)}%</span>
                    </div>
                    <div className="pt-4 border-t border-white/10">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-white/60">Est. Annual Return</span>
                        <span className="text-xl font-mono text-[#4ADE80]" data-testid="investment-return">
                          {formatCurrencyDetailed((investmentAmount * property.annual_return) / 100)}
                        </span>
                      </div>
                      {property.is_off_plan && property.projected_value && (
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-white/60 text-sm">Est. Value at Completion</span>
                          <span className="text-lg font-mono text-[#4ADE80]">
                            {formatCurrencyDetailed((investmentAmount / property.current_value) * property.projected_value)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="space-y-3">
                    <Button 
                      onClick={handleInvestment}
                      disabled={investing || property.available_shares === 0}
                      className="w-full bg-white text-black hover:bg-gray-200 font-medium py-6 text-base rounded-full disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 hover:scale-105"
                      data-testid="invest-button"
                    >
                      {investing ? 'Processing...' : property.available_shares === 0 ? 'Sold Out' : property.is_off_plan ? 'Invest in Construction' : 'Invest Now'}
                    </Button>
                    <p className="text-xs text-white/40 text-center">
                      {property.available_shares.toFixed(2)} shares available
                    </p>
                    {property.is_off_plan && (
                      <p className="text-xs text-[#4ADE80] text-center">
                        Early-stage investment opportunity • Higher appreciation potential
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Investment Highlights */}
              {property.is_off_plan && (
                <div className="mt-6 bg-[#0A0A0A] border border-white/5 rounded-xl p-6">
                  <h3 className="text-lg font-medium mb-4">Why Invest Off-Plan?</h3>
                  <ul className="space-y-3 text-sm text-white/60">
                    <li className="flex items-start space-x-2">
                      <span className="text-[#4ADE80] mt-0.5">✓</span>
                      <span>Lower entry price compared to completed properties</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-[#4ADE80] mt-0.5">✓</span>
                      <span>Higher appreciation potential during construction</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-[#4ADE80] mt-0.5">✓</span>
                      <span>Invest in specific construction stages</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-[#4ADE80] mt-0.5">✓</span>
                      <span>12-24 month investment horizon</span>
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}